// src/features/drug-panels/components/DrugPanelMultiSelect.tsx
import { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";
import type { DrugPanel } from "../services/drugPanelApi";

interface DrugPanelMultiSelectProps {
  availablePanels: DrugPanel[];
  selectedPanelIds: number[];
  onChange: (selectedIds: number[]) => void;
  disabled?: boolean;
}

export default function DrugPanelMultiSelect({
  availablePanels,
  selectedPanelIds,
  onChange,
  disabled = false,
}: DrugPanelMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleTogglePanel = (panelId: number) => {
    const updated = selectedPanelIds.includes(panelId)
      ? selectedPanelIds.filter((id) => id !== panelId)
      : [...selectedPanelIds, panelId];
    onChange(updated);
  };

  const selectedPanels = availablePanels.filter((panel) =>
    selectedPanelIds.includes(panel.id)
  );

  const activePanels = availablePanels.filter((panel) => panel.status === "ACTIVE");

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full border border-gray-300 rounded px-3 py-2 text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white disabled:opacity-50 hover:border-gray-400 transition-colors"
      >
        <span className="text-sm">
          {selectedPanels.length === 0
            ? "Select drug panels..."
            : `${selectedPanels.length} panel(s) selected`}
        </span>
        <ChevronDown
          size={20}
          className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg">
          <div className="max-h-64 overflow-y-auto">
            {activePanels.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-sm">
                No active drug panels available
              </div>
            ) : (
              activePanels.map((panel) => {
                const isSelected = selectedPanelIds.includes(panel.id);
                return (
                  <label
                    key={panel.id}
                    className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleTogglePanel(panel.id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 cursor-pointer"
                    />
                    <span className="ml-3 flex-1">
                      <div className="font-medium text-sm">{panel.panelName}</div>
                      <div className="text-xs text-gray-500">
                        {panel.numComponents} components
                      </div>
                    </span>
                    {isSelected && (
                      <span className="text-blue-600 font-bold">✓</span>
                    )}
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}

      {selectedPanels.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedPanels.map((panel) => (
            <span
              key={panel.id}
              className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
            >
              {panel.panelName}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
